import { useEffect, useState } from 'react';
import Waveform from './Waveform';

/**
 * The hero voice demo — tap the mic, Lincoln "listens" then "responds".
 * Cycles through a set of demo prompts. Pure UI state — no real AI wired up
 * here; call window.claude.complete from a parent if you want it live.
 */
export default function VoiceDemo({ size = 'lg' }) {
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState(0); // 0 idle, 1 listening, 2 thinking, 3 speaking
  const [transcriptIdx, setTranscriptIdx] = useState(0);

  const prompts = [
    "Explain photosynthesis like I'm 10",
    "Help me solve x² + 5x + 6 = 0",
    "Quiz me on European capitals",
    "Translate 'good morning' to Finnish",
  ];

  useEffect(() => {
    if (!active) { setPhase(0); return; }
    const t1 = setTimeout(() => setPhase(2), 1800);
    const t2 = setTimeout(() => setPhase(3), 3000);
    const t3 = setTimeout(() => {
      setPhase(0); setActive(false);
      setTranscriptIdx((i) => (i + 1) % prompts.length);
    }, 6500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [active, prompts.length]);

  const dim = size === 'lg' ? 132 : 92;

  return (
    <div
      className="w-full flex flex-col items-center gap-4 rounded-[28px] p-7 backdrop-blur-xl"
      style={{
        maxWidth: 440,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.12)',
      }}
    >
      <div
        className="inline-flex items-center gap-2 rounded-full font-semibold tracking-wider"
        style={{ padding: '6px 14px', background: 'rgba(34,181,114,0.18)', color: '#7be3b0', fontSize: 12 }}
      >
        <span
          className="block rounded-full"
          style={{ width: 8, height: 8, background: '#22b572', animation: 'lc-pulse-ring 2s infinite' }}
        />
        {phase === 0 && 'TAP TO TALK'}
        {phase === 1 && 'LISTENING…'}
        {phase === 2 && 'THINKING…'}
        {phase === 3 && 'LINCOLN IS SPEAKING'}
      </div>

      <button
        type="button"
        onClick={() => { if (!active) { setActive(true); setPhase(1); } }}
        className="relative rounded-full transition-all inline-flex items-center justify-center"
        style={{
          width: dim,
          height: dim,
          background: active
            ? 'radial-gradient(circle, #f5c63a 0%, #f0b80c 70%)'
            : 'linear-gradient(135deg, #f0b80c, #d99a06)',
          boxShadow: active
            ? '0 0 0 14px rgba(240,184,12,0.16), 0 0 0 32px rgba(240,184,12,0.08), 0 12px 32px rgba(240,184,12,0.4)'
            : '0 8px 24px rgba(240,184,12,0.35)',
        }}
      >
        {active && [0, 1].map((i) => (
          <span
            key={i}
            className="absolute rounded-full"
            style={{ inset: -8, border: '2px solid rgba(240,184,12,0.5)', animation: `lc-pulse-ring 2s ${i * 0.7}s ease-out infinite` }}
          />
        ))}
        <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="#0b1f38" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="relative">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      </button>

      <div className="w-full flex items-center justify-center" style={{ height: 60 }}>
        {active ? (
          <Waveform bars={32} large color="#f0b80c" active />
        ) : (
          <div className="text-center">
            <div className="uppercase tracking-widest" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>Try saying</div>
            <div className="italic" style={{ fontSize: 16, color: '#fff' }}>"{prompts[transcriptIdx]}"</div>
          </div>
        )}
      </div>

      {!active && (
        <div className="flex flex-wrap justify-center gap-2">
          {prompts.slice(0, 3).map((p, i) => (
            <button
              key={p}
              type="button"
              onClick={() => { setTranscriptIdx(i); setActive(true); setPhase(1); }}
              className="rounded-full font-medium transition-all"
              style={{
                fontSize: 12,
                padding: '8px 12px',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.8)',
                background: 'rgba(255,255,255,0.04)',
              }}
            >
              {p.length > 30 ? p.slice(0, 30) + '…' : p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
